import { AxiosInstance } from 'axios';
import { axiosInstance } from '../axios-instance';
import { ENDPOINTS } from '../endpoint';
import { BaseApiService } from './base-api.service';
import type { MenuConfig, SubscriptionPaymentSettings, SubscriptionPlanType } from '@repo/types';

export interface SubscriptionPlanTier {
  id: string;
  title?: string;
  subTitle?: string;
  maxTrees: number;
  price: number;
  benefits: string[];
  benefitsHtml?: string;
  maxAlignments: number;  // -1 = illimité, 0 = interdit (BASIC)
  maxEpaysages: number;   // -1 = illimité, 0 = interdit (BASIC)
  /** ID du Product Stripe associé à ce plan */
  stripeProductId?: string;
  /** ID du Price Stripe actif pour ce tier — source de vérité pour le checkout */
  stripePriceId?: string;
}

export interface SubscriptionPlanConfig {
  tiers: SubscriptionPlanTier[];
  maxAlignments: number; // -1 means unlimited
  maxEpaysages: number;  // -1 means unlimited
}

export interface SubscriptionConfig {
  plans: {
    BASIC: SubscriptionPlanConfig;
    ADVANCED: SubscriptionPlanConfig;
    [key: string]: SubscriptionPlanConfig;
  };
  requirePaymentBeforeSignup?: boolean;
  allowFreeTrial?: boolean;
  freeTrialDurationDays?: number;
  freeTrialMaxTrees?: number;
  freeTrialMaxFiches?: number;
}

export class SettingsService extends BaseApiService {
  constructor(axios: AxiosInstance = axiosInstance) {
    super(ENDPOINTS.SETTINGS.BASE, axios);
  }
  /**
   * Récupère la configuration des abonnements
   */
  async getSubscriptionConfig(): Promise<SubscriptionConfig> {
    const { data } = await this.axios.get(`${ENDPOINTS.SETTINGS.SUBSCRIPTION}`);
    return data.data;
  }

  /**
   * Récupère la configuration publique (sans auth) pour l'affichage marketing
   */
  async getPublicSubscriptionConfig(): Promise<SubscriptionConfig> {
    const { data } = await this.axios.get(`${ENDPOINTS.SETTINGS.SUBSCRIPTION_PUBLIC}`);
    return data.data;
  }

  /**
   * Met à jour la configuration des abonnements
   */
  async updateSubscriptionConfig(config: SubscriptionConfig): Promise<SubscriptionConfig> {
    const { data } = await this.axios.put(`${ENDPOINTS.SETTINGS.SUBSCRIPTION}`, config);
    return data.data;
  }

  /** Enregistre UN palier (création ou modification) ; l'API ne touche pas aux autres. */
  async saveSubscriptionTier(planType: SubscriptionPlanType, tier: SubscriptionPlanTier): Promise<SubscriptionPlanTier> {
    const { id, stripeProductId: _p, stripePriceId: _s, ...body } = tier;
    const { data } = await this.axios.put(ENDPOINTS.SETTINGS.SUBSCRIPTION_TIER(planType, id), body);
    return data.data;
  }

  /** Retire un palier de la vente (archivage côté API). */
  async archiveSubscriptionTier(planType: SubscriptionPlanType, tierId: string): Promise<void> {
    await this.axios.delete(ENDPOINTS.SETTINGS.SUBSCRIPTION_TIER(planType, tierId));
  }

  /** Réglages de paiement / essai gratuit : seuls les champs fournis sont modifiés. */
  async updateSubscriptionPaymentSettings(settings: SubscriptionPaymentSettings): Promise<SubscriptionConfig> {
    const { data } = await this.axios.put(ENDPOINTS.SETTINGS.SUBSCRIPTION_PAYMENT_SETTINGS, settings);
    return data.data;
  }

  /** Visibilité des menus par rôle (réglée dans l'espace /superadmin). */
  async getMenuConfig(): Promise<MenuConfig> {
    const { data } = await this.axios.get(ENDPOINTS.SETTINGS.MENU_CONFIG);
    return data.data ?? {};
  }

  async updateMenuConfig(menuConfig: MenuConfig): Promise<MenuConfig> {
    const { data } = await this.axios.put(ENDPOINTS.SETTINGS.MENU_CONFIG, { menu_config: menuConfig });
    return data.data;
  }

  /**
   * Initialise les paramètres par défaut
   */
  async initializeDefaultSettings(): Promise<SubscriptionConfig> {
    const { data } = await this.axios.put(`${ENDPOINTS.SETTINGS.SUBSCRIPTION_INITIALIZE}`);
    return data.data;
  }

  /**
   * Déclenche la synchronisation Stripe (produits + prices).
   * Stocke stripePriceId/stripeProductId dans les tiers et archive les anciens prices.
   * Requiert le rôle ADMIN.
   */
  async syncStripe(): Promise<{
    created: number;
    updated: number;
    archived: number;
    errors: Array<{ planType: string; tierId: string; error: string }>;
  }> {
    const { data } = await this.axios.post(`${ENDPOINTS.SETTINGS.SUBSCRIPTION}/stripe-sync`);
    return data;
  }
}

export const settingsService = new SettingsService();
